import type { SeedingCampaignDetail } from '../types';
import type { RecruitApplyValidationCaseKey } from './RecruitApplyValidationDialog';

/**
 * 큐레이터 계정 상태 — BE 연동 전 mock 단계 전달 인터페이스.
 *
 * BE 연동 후엔 서비스 레이어의 실제 DTO 로 교체 예정.
 */
export interface CuratorAccountStatus {
  /** 페널티 여부 (콘텐츠 제출 기한 초과 캠페인 보유 여부) */
  hasPenalty: boolean;
  /** 페널티 사유 (BE 응답 동적) */
  penaltyReason?: string;
  /** 현재 진행 중인 캠페인 수 */
  activeCampaignCount: number;
  /** 사용자 등록 SNS 플랫폼 목록 (예: ['Instagram', 'Youtube']) */
  registeredSnsPlatforms: string[];
  /** 배송지 등록 여부 */
  hasShippingAddress: boolean;
}

/** 캠페인 요구 SNS 채널 목록 (BE 연동 전 임시 타입) */
export interface CampaignSnsRequirement {
  /** 요구 채널명 목록 (예: ['Instagram']) */
  requiredPlatforms: string[];
}

export interface RecruitApplyValidationResult {
  caseKey: RecruitApplyValidationCaseKey;
  /** 패널티 사유 (PENALTY 케이스 전용) */
  penaltyReason?: string;
  /** 미등록 SNS 채널명 (SNS_MISSING 케이스 전용) */
  missingSnsName?: string;
}

/**
 * 모집형 신청 클라이언트 Validation.
 *
 * plan §2 순서대로 순차 평가:
 * 1. 페널티 → PENALTY (차단)
 * 2. 진행 캠페인 초과 (≥10) → OVER_LIMIT (차단)
 * 3. 옵션 미선택 → OPTIONS_MISSING (사용자 수정 가능)
 * 4. 배송지 미등록 → ADDRESS_MISSING (등록 페이지)
 * 5. SNS 채널 미등록 → SNS_MISSING (등록 페이지)
 * 6. 모두 통과 → null
 *
 * @param detail - 시딩 캠페인 상세 (옵션 선택 상태 포함)
 * @param accountStatus - 큐레이터 계정 상태
 * @param snsRequirement - 캠페인 요구 SNS 채널 (없으면 SNS 검사 스킵)
 * @returns 첫 번째 실패 케이스 또는 null (통과)
 */
export function evaluateRecruitApplyValidation(
  detail: SeedingCampaignDetail,
  accountStatus: CuratorAccountStatus,
  snsRequirement?: CampaignSnsRequirement,
): RecruitApplyValidationResult | null {
  // 1. 페널티
  if (accountStatus.hasPenalty) {
    return {
      caseKey: 'PENALTY',
      penaltyReason: accountStatus.penaltyReason,
    };
  }

  // 2. 최대 진행 가능 캠페인 초과 (10개)
  if (accountStatus.activeCampaignCount >= 10) {
    return { caseKey: 'OVER_LIMIT' };
  }

  // 3. 옵션 미선택 — 품절 상품은 선택 대상이 아니므로 제외. 선택 가능한 상품 중 1개라도
  //    selectedOption 이 null 이면 차단. (품절 제외는 buildSeedingMutationBody 와 동일 규칙)
  //    단품(isSingleProduct)은 옵션 선택 자체가 없으므로 제외. (SID-4296/4314)
  const hasUnselectedOption = detail.products.some(
    (p) => !p.soldOut && !p.isSingleProduct && p.selectedOption === null,
  );
  if (hasUnselectedOption) {
    return { caseKey: 'OPTIONS_MISSING' };
  }

  // 4. 배송지 미등록
  if (!accountStatus.hasShippingAddress) {
    return { caseKey: 'ADDRESS_MISSING' };
  }

  // 5. SNS 채널 미등록
  // PRD §5-1 SNS 유효성: 요구 채널과 등록 채널의 "교집합이 하나도 없으면" 신청 불가.
  // (요구 채널 전부를 등록해야 하는 것이 아니라, 일치 채널이 1개라도 있으면 통과)
  if (snsRequirement && snsRequirement.requiredPlatforms.length > 0) {
    const registered = new Set(accountStatus.registeredSnsPlatforms);
    const hasAnyMatch = snsRequirement.requiredPlatforms.some((p) => registered.has(p));
    if (!hasAnyMatch) {
      // 안내 팝업에는 요구 채널 중 첫 번째를 대표로 노출.
      return { caseKey: 'SNS_MISSING', missingSnsName: snsRequirement.requiredPlatforms[0] };
    }
  }

  // 6. 통과
  return null;
}
